'use client';

import { useEffect, useState } from 'react';
import {
  useLearningSource,
  type LearningItem,
  type LearningRow,
} from '@akademi/site-builder-runtime';
import { prettify } from './prettify';

/**
 * Three-state row fetcher specific to {@link LearningSource}. Every
 * learning rail block uses this hook so the loading/empty/loaded
 * branches stay uniform — the lower-level {@code useAsyncResource}
 * doesn't bake in label resolution or the stale signal that the
 * legacy block code relied on.
 *
 * <p>Direct port of ThemeMaker's
 * {@code components/blocks/_shared/useLearningRow.ts}, rewired to
 * the runtime adapter.
 */
export type LearningRowState =
  | { kind: 'loading'; label: string }
  | { kind: 'loaded'; label: string; items: LearningItem[]; stale: boolean }
  | { kind: 'empty'; label: string };

export interface UseLearningRowOpts {
  source: string;
  /** Max items to fetch. Hosts typically clamp server-side. */
  limit?: number;
  /** Optional override label — wins over the server's label and the
   *  prettified fallback. */
  authorTitle?: string;
}

export function useLearningRow(opts: UseLearningRowOpts): LearningRowState {
  const { source, limit = 12, authorTitle } = opts;
  const learning = useLearningSource();

  const [state, setState] = useState<LearningRowState>({
    kind: 'loading',
    label: authorTitle ?? prettify(source),
  });

  useEffect(() => {
    if (!learning) {
      setState({ kind: 'empty', label: authorTitle ?? prettify(source) });
      return;
    }
    const controller = new AbortController();
    setState({ kind: 'loading', label: authorTitle ?? prettify(source) });

    (async () => {
      try {
        const row: LearningRow | null = await learning.fetchRow({
          source,
          limit,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        const label = authorTitle || row?.label || prettify(source);
        if (!row || row.items.length === 0) {
          setState({ kind: 'empty', label });
          return;
        }
        setState({ kind: 'loaded', label, items: row.items, stale: row.stale === true });
      } catch (e) {
        if (controller.signal.aborted) return;
        if (e instanceof DOMException && e.name === 'AbortError') return;
        setState({ kind: 'empty', label: authorTitle ?? prettify(source) });
      }
    })();

    return () => controller.abort();
  }, [source, limit, authorTitle, learning]);

  return state;
}
